import Vue from 'vue';
import Vuex, { StoreOptions } from 'vuex';
import { authenticate, fetchHistory, Event } from '@/data-service';
import { RootState } from './types';
import { saveState, getState, deleteState, getDayLabel, formatMinutes } from '@/utils';
import audioModule from './audio-player';

Vue.use(Vuex);

// Mutations
const SET_NEXT_TOKEN = 'setNextToken';
const ADD_EVENTS = 'addEvents';
const DELETE_AUTH_TOKEN = 'deleteAuthToken';
const DELETE_EVENTS = 'deleteEvents';
const UPDATE_EXPIRY = 'updateExpiry';
const REMOVE_TIMER = 'removeInterval';
const ADD_TIMER = 'addInterval';
const SET_SESSION_TOKEN = 'setSessionToken';
const SET_SESSION_EXPIRY = 'setSessionExpiry';

// Actions
const AUTHENTICATE = 'authenticate';
const FETCH_HISTORY = 'fetchHistory';
const LEAVE_SESSION = 'leaveSession';
const START_TIMER = 'startTimer';

// Getters
const IS_LOGGED_IN = 'isLoggedIn';
const GROUPED_EVENTS = 'groupedEvents';
const EXPIRY_COUNTDOWN = 'expiryCountdown';

// Storage keys
const SESSION_TOKEN = 'sessionToken';
const SESSION_EXPIRY = 'sessionExpiry';

const EXPIRY_LABEL_UPDATE_INT = 1000 * 10;

const state: RootState = {
  sessionToken: getState(SESSION_TOKEN),
  sessionExpiry: getState(SESSION_EXPIRY),
  events: [],
  timeRemaining: null,
  timerId: null
};

const storeOptions: StoreOptions<RootState> = {
  state,
  mutations: {
    [SET_SESSION_TOKEN](state, token: string) {
      state.sessionToken = token;
      saveState(SESSION_TOKEN, token);
    },
    [SET_SESSION_EXPIRY](state, date: Date) {
      state.sessionExpiry = date;
      saveState(SESSION_EXPIRY, date);
    },
    [SET_NEXT_TOKEN](state, token: string) {
      state.nextToken = token;
    },
    [ADD_EVENTS](state, events: Event[]) {
      if (!state.events.length) {
        state.events = events;
      } else {
        const merged = state.events.concat(events);
        state.events = merged;
      }
    },
    [DELETE_EVENTS]() {
      state.events = [];
      state.nextToken = undefined;
    },
    [DELETE_AUTH_TOKEN]() {
      state.sessionToken = undefined;
      state.sessionExpiry = undefined;
      deleteState(SESSION_TOKEN);
      deleteState(SESSION_EXPIRY);
    },
    [UPDATE_EXPIRY]() {
      if (!state.sessionExpiry) {
        return;
      }
      if (state.timeRemaining && state.timeRemaining < 0) {
        deleteState(SESSION_TOKEN);
        deleteState(SESSION_EXPIRY);
        return;
      }
      state.timeRemaining =
        (new Date(state.sessionExpiry).getTime() - new Date().getTime()) / 1000 / 60;
    },
    [ADD_TIMER](state, id: number) {
      state.timerId = id;
    },
    [REMOVE_TIMER](state) {
      if (state.timerId) {
        clearInterval(state.timerId);
      }
      state.timerId = null;
    }
  },
  actions: {
    [AUTHENTICATE]({ commit }, shareCode: string) {
      return authenticate(shareCode).then(result => {
        commit(SET_SESSION_TOKEN, result.token);
        commit(SET_SESSION_EXPIRY, result.expiry);
      });
    },
    [FETCH_HISTORY]({ state, commit, dispatch }, limit = 10) {
      if (!state.sessionToken) {
        return;
      }
      if (!state.timerId) {
        dispatch(START_TIMER);
      }
      return fetchHistory(state.sessionToken, limit, state.nextToken).then(
        ({ events, nextToken }) => {
          commit(ADD_EVENTS, events);
          commit(SET_NEXT_TOKEN, nextToken);
        }
      );
    },
    [LEAVE_SESSION]({ commit }) {
      commit(DELETE_AUTH_TOKEN);
      commit(DELETE_EVENTS);
      commit(REMOVE_TIMER);
    },
    [START_TIMER]({ commit }) {
      commit(UPDATE_EXPIRY);
      const id = setInterval(() => {
        commit(UPDATE_EXPIRY);
      }, EXPIRY_LABEL_UPDATE_INT);
      commit(ADD_TIMER, id);
    }
  },
  getters: {
    [IS_LOGGED_IN]: state =>
      !!state.sessionToken && !!state.sessionExpiry && new Date() < new Date(state.sessionExpiry),
    [GROUPED_EVENTS]: state => {
      return Object.entries(
        state.events.reduce((acc: { [key: number]: { label: string; events: Event[] } }, event) => {
          const day = new Date(event.recordedAt).setHours(0, 0, 0, 0);
          const dayLabel = getDayLabel(event.recordedAt);
          acc[day] = acc[day] || {};
          acc[day].label = acc[day].label || dayLabel;
          acc[day].events = acc[day].events || [];
          acc[day].events.push(event);
          return acc;
        }, {})
      )
        .sort((a, b) => parseInt(b[0]) - parseInt(a[0]))
        .map(arr => ({ [arr[0]]: arr[1] }));
    },
    [EXPIRY_COUNTDOWN]: state => (state.timeRemaining ? formatMinutes(state.timeRemaining) : '')
  },
  modules: {
    audio: audioModule
  },
  strict: process.env.NODE_ENV !== 'production'
};

const store = new Vuex.Store(storeOptions);

export default store;

export {
  AUTHENTICATE,
  FETCH_HISTORY,
  LEAVE_SESSION,
  IS_LOGGED_IN,
  GROUPED_EVENTS,
  EXPIRY_COUNTDOWN,
  SESSION_TOKEN,
  UPDATE_EXPIRY
};

export {
  ADD_RECORDING,
  CURRENT_RECORDING,
  CURRENT_POSITION,
  IS_PLAYING,
  RECORDING_DURATION,
  SET_CURRENT_POSITION,
  SET_CURRENT_RECORDING,
  SET_REQUESTED_POSITION,
  UPDATE_RECORDING_DURATION,
  REQUESTED_POSITION
} from './audio-player';
